import type { InterleavedWord } from 'js/src/agent/tools/transcript-tokens';
import { interleavedTokenToString } from 'js/src/agent/tools/transcript-tokens';

/**
 * The transcript context an asset's scene sits in: the scene group's interleaved tokens plus the
 * translation from video time to transcript time (which honours cuts in the narration), so an
 * asset's appearance point can be named as the word being spoken. Undefined for text-on-media
 * scenes, which have no transcript.
 */
export interface AssetTimingTranscriptContext {
    tokens: InterleavedWord[];
    videoTimeToTranscriptTime: (videoTimeMs: number) => number;
}

/** The subset of an asset its appearance point is read from. */
interface AppearingAsset {
    /** When the asset lands, relative to its container — group stagger included. */
    appliedIntroAnimationOffset: number;
    /** Set on every asset on screen where a match cut joins two scenes. */
    isMatchCutFrozen?: boolean;
}

/** The subset of an asset the timing description reads. */
interface TimedAsset extends AppearingAsset {
    /** The scene or subscene the asset sits on; its start is the origin the offset is applied from. */
    luminaryParent: { startTime: number } | null;
}

/**
 * When an asset actually appears, relative to its container. A match cut holds every asset on
 * screen at the cut in place for its whole scene, and Luminary's applied offset then reads the
 * morph at the cut rather than the (skipped) entrance, so a held asset is on screen from the start.
 */
export function getAssetAppearanceOffset(asset: AppearingAsset): number {
    return asset.isMatchCutFrozen ? 0 : asset.appliedIntroAnimationOffset;
}

/**
 * How an asset's appearance point reads in the video state, or undefined when it simply appears
 * with its scene (the overwhelmingly common case — emitting "0ms" on every asset would be noise).
 *
 * Named as the transcript word being spoken when the asset lands, so the read vocabulary matches
 * the `start_words` the agent writes back through set_text / set_group_asset. Falls back to
 * milliseconds when there is no transcript (text-on-media) or the offset lands past the last word,
 * matching `start_time_ms`.
 */
export function describeAssetAppearsAt(
    asset: TimedAsset,
    transcript: AssetTimingTranscriptContext | undefined,
): string | undefined {
    const introOffset = getAssetAppearanceOffset(asset);
    if (!Number.isFinite(introOffset) || introOffset <= 0) {
        return undefined;
    }

    if (transcript && asset.luminaryParent) {
        const introInTranscriptTime = transcript.videoTimeToTranscriptTime(
            asset.luminaryParent.startTime + introOffset,
        );
        const spokenToken = transcript.tokens.find(
            (token) =>
                token.endTime > introInTranscriptTime && token.startTime <= introInTranscriptTime,
        );
        if (spokenToken) {
            return `"${interleavedTokenToString(spokenToken)}"`;
        }
    }

    return `${Math.round(introOffset)}ms into the scene`;
}
